import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  TextField
} from '@mui/material';
import {
  Business,
  Store,
  AccountBalance,
  People,
  Assignment,
  LocalShipping,
  PendingActions,
  Add,
  Group,
  Dashboard as DashboardIcon,
  ListAlt,
  ReceiptLong,
  Work,
  Inventory,
  Assessment,
  History
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State for Bill search
  const [billName, setBillName] = useState('');
  const [billNumber, setBillNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);

  const handleBillSearch = async (e) => {
    e.preventDefault();
    // If both fields are blank, do nothing
    if (!billName.trim() && !billNumber.trim()) {
      return;
    }
    setLoading(true);
    setSearchResult(null);
    try {
      const res = await api.get(`/api/shop/orders?billName=${encodeURIComponent(billName)}&billNumber=${encodeURIComponent(billNumber)}`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        setSearchResult(res.data[0]);
      } else {
        setSearchResult('notfound');
      }
    } catch (err) {
      setSearchResult('notfound');
    }
    setLoading(false);
  };

  const getQuickActions = () => {
    const actions = [];

    // Only show 22K and 24K Gold for transaction dashboard (for all users)
    if (user?.role === 0 || user?.subadminType === 'transaction') {
      actions.push(
        { title: '22K Gold', icon: <AccountBalance />, path: '/transaction/22k', color: '#7b1fa2' },
        { title: '24K Gold', icon: <AccountBalance />, path: '/transaction/24k', color: '#c2185b' },
        { title: 'Other Transaction 1', icon: <Work />, path: '/transaction/other1', color: '#009688' },
        { title: 'Other Transaction 2', icon: <Assessment />, path: '/transaction/other2', color: '#ff9800' },
        { title: 'Total Balance', icon: <Store />, path: '/transaction/total-balance', color: '#3f51b5' },
        { title: 'Previous Balance', icon: <Inventory />, path: '/transaction/previous-balance', color: '#607d8b' }
      );
      return actions;
    }

    // Universal dashboard actions (for all roles except transaction)
    actions.push(
  { title: 'Order Entry', icon: <Add />, path: '/shop/orders/add', color: '#1976d2' },
  { title: 'Add Item', icon: <Add />, path: '/shop/items/add', color: '#1976d2' },
  { title: 'Despatch', icon: <LocalShipping />, path: user?.subadminType === 'shop' ? '/shop/despatch' : '/karigar/despatch', color: '#c2185b' },
  { title: 'Item Issue', icon: <Assignment />, path: user?.subadminType === 'shop' ? '/shop/item-issue' : '/karigar/item-issue', color: '#7b1fa2' },
  { title: 'Work Receipt', icon: <ReceiptLong />, path: user?.subadminType === 'shop' ? '/shop/work-receipt' : '/karigar/work-receipt', color: '#388e3c' },
  { title: 'Metal Issue', icon: <Inventory />, path: user?.subadminType === 'shop' ? '/shop/metal-issue' : '/karigar/metal-issue', color: '#f57c00' },
  { title: 'Karigar Transaction', icon: <Assessment />, path: user?.subadminType === 'shop' ? '/shop/karigar-transactions' : '/karigar/transactions', color: '#1976d2' }
    );

    if (user?.role === 0) {
      // Admin actions
      actions.push(
        { title: 'User Management', icon: <People />, path: '/admin/users', color: '#1976d2' },
        { title: 'Activity Log', icon: <History />, path: '/admin/activity-logs', color: '#ff9800' },
        { title: 'Karigar List', icon: <Business />, path: '/karigar', color: '#388e3c' },
        { title: 'Shop Orders', icon: <Store />, path: '/shop/orders', color: '#f57c00' },
        { title: '22K Transaction', icon: <AccountBalance />, path: '/transaction/22k', color: '#7b1fa2' },
        { title: '24K Transaction', icon: <AccountBalance />, path: '/transaction/24k', color: '#c2185b' }
      );
    } else {
      // Subadmin actions based on type
      switch (user?.subadminType) {
        case 'karigar':
          actions.push(
            { title: 'Order List', icon: <ListAlt />, path: '/karigar/orders', color: '#1976d2' },
            { title: 'Direct Order List', icon: <ListAlt />, path: '/karigar/direct-orders', color: '#388e3c' },
            { title: 'Direct Order Entry', icon: <Assignment />, path: '/karigar/direct-order', color: '#388e3c' },
            { title: 'Update Order', icon: <Assignment />, path: '/update-order', color: '#1976d2' },
            { title: 'Update Direct Order', icon: <Assignment />, path: '/update-direct-order', color: '#388e3c' },
            { title: 'Karigar List', icon: <Business />, path: '/karigar', color: '#388e3c' },
            { title: 'Pending Issues', icon: <PendingActions />, path: '/karigar/pending-issues', color: '#d32f2f' },
            { title: 'Pending List', icon: <PendingActions />, path: '/karigar/pending-list', color: '#d32f2f' }
          );
          break;
        case 'shop':
          actions.push(
            { title: 'Order List', icon: <ListAlt />, path: '/shop/orders', color: '#1976d2' },
            { title: 'Direct Order List', icon: <ListAlt />, path: '/shop/direct-orders', color: '#388e3c' },
            { title: 'Direct Order Entry', icon: <Assignment />, path: '/shop/direct-order', color: '#388e3c' },
            { title: 'Update Order', icon: <Assignment />, path: '/update-order', color: '#1976d2' },
            { title: 'Update Direct Order', icon: <Assignment />, path: '/update-direct-order', color: '#388e3c' },
            { title: 'Karigar List', icon: <Business />, path: '/shop', color: '#388e3c' },
            { title: 'Pending Issues', icon: <PendingActions />, path: '/shop/pending-issues', color: '#d32f2f' },
            { title: 'Pending List', icon: <PendingActions />, path: '/shop/pending-list', color: '#d32f2f' }
          );
          break;
        case 'transaction':
          actions.push(
            { title: '22K Gold', icon: <AccountBalance />, path: '/transaction/22k', color: '#7b1fa2' },
            { title: '24K Gold', icon: <AccountBalance />, path: '/transaction/24k', color: '#c2185b' }
          );
          break;
        default:
          actions.push({ title: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', color: '#1976d2' });
      }
    }

    return actions;
  };

  const getWelcomeMessage = () => {
    if (user?.role === 0) {
      return 'Welcome to TKM Group MIS Admin Dashboard';
    } else {
      const moduleName = user?.subadminType?.charAt(0).toUpperCase() + user?.subadminType?.slice(1);
      return `Welcome to TKM Group MIS - ${moduleName} Module`;
    }
  };

  const getRecentActivity = () => {
    const activities = [
      { text: 'System login successful', time: 'Just now', type: 'info' },
      { text: 'Database connection established', time: '2 minutes ago', type: 'success' },
      { text: 'User session started', time: '5 minutes ago', type: 'info' }
    ];

    return activities;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left: Quick Actions Cards */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2} sx={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
              {getQuickActions().map((action, index) => (
                <Grid item xs={6} sm={3} md={3} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      minHeight: 100,
                      maxWidth: 180,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate(action.path)}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 1, px: 1 }}>
                      <Box sx={{ color: action.color, mb: 0.5, fontSize: 28 }}>
                        {action.icon}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 14 }}>
                        {action.title}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        {/* Right: Bill Search Box */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Search Bills
            </Typography>
            <Box component="form" onSubmit={handleBillSearch}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Bill Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={billName}
                    onChange={e => setBillName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Bill Number"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={billNumber}
                    onChange={e => setBillNumber(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {loading ? 'Searching...' : 'Search'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
            {(billName.trim() || billNumber.trim()) && searchResult === 'notfound' && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3f3', borderRadius: 2 }}>
                <Typography color="error">No order found for this Bill Name and Number.</Typography>
              </Box>
            )}
            {(billName.trim() || billNumber.trim()) && searchResult && searchResult !== 'notfound' && (
              <Paper sx={{ mt: 2, p: 2, backgroundColor: 'background.paper', borderRadius: 2, overflowX: 'auto', maxWidth: 700, width: '100%', mx: 'auto' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Order Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField label="Bill Name" value={searchResult.billName} fullWidth size="small" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField label="Bill Number" value={searchResult.billNumber} fullWidth size="small" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField label="Order Date" value={searchResult.orderDate ? searchResult.orderDate.slice(0,10) : ''} fullWidth size="small" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField label="Delivery Date" value={searchResult.deliveryDate ? searchResult.deliveryDate.slice(0,10) : ''} fullWidth size="small" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField label="Required Date" value={searchResult.requiredDate ? searchResult.requiredDate.slice(0,10) : ''} fullWidth size="small" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField label="Status" value={searchResult.status} fullWidth size="small" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Agent Name" value={searchResult.agentName} fullWidth size="small" InputProps={{ readOnly: true }} />
                    </Grid>
                    {searchResult.customer && (
                      <>
                        <Grid item xs={6}>
                          <TextField label="Customer Name" value={searchResult.customer.name} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField label="Karigar ID" value={searchResult.customer.karigarId} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField label="Address" value={searchResult.customer.address} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField label="Phone Number" value={searchResult.customer.phoneNumber} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Items</Typography>
                  <Grid container spacing={2}>
                    {searchResult.items && searchResult.items.map((item, idx) => (
                      <React.Fragment key={idx}>
                        <Grid item xs={12} md={4}>
                          <TextField label="Item Name" value={item.itemName} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField label="Qty" value={item.quantity} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField label="Unit" value={item.unit} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField label="Weight" value={item.itemWeight} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField label="Karat" value={item.karat} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField label="Total" value={item.total} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField label="Net Amt" value={item.netAmount} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField label="Total Amt" value={item.totalAmount} fullWidth size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                      </React.Fragment>
                    ))}
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Order Total: ₹{searchResult.totalAmount}</Typography>
                </Box>
              </Paper>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 